import { gql } from '@apollo/client';
import type { Trace as ProtobufTrace } from 'apollo-reporting-protobuf';
import _ from 'lodash';

import { isConnectorSubgraphViaName } from 'src/app/graph/explorerPage/helpers/queryPlanHelpers';
import { format } from 'src/lib/format';
import * as Types from 'src/lib/graphqlTypes/types';

const traceNodeErrorFragment = gql`
  fragment TraceNodeErrorFragment on TraceError {
    message
    locations {
      line
      column
    }
  }
`;

export const traceNodeFragment = gql`
  fragment TraceNodeFragment on TraceNode {
    id
    attributes {
      key
      value
    }
    kind
    subgraphName
    parentId
    startTime
    endTime
    isFetch
    isDeferredFetch
    subgraphStartTime
    subgraphEndTime
    childCount
    childrenAreTruncated
    cacheMaxAgeMs
    key
    type
    errors {
      ...TraceNodeErrorFragment
    }
  }
  ${traceNodeErrorFragment}
`;

export const traceFragment = gql`
  fragment TraceFragment on Trace {
    id
    startTime
    endTime
    operationName
    cacheMaxAgeMs
    isIncomplete
    agentVersion
    root {
      ...TraceNodeFragment
      descendants {
        ...TraceNodeFragment
      }
    }
  }
  ${traceNodeFragment}
`;

interface TraceTreeInheritedProperties {
  isDeferred?: boolean;
}

export class TraceTreeNode {
  id: string;
  attributes: Types.StringToString[] = [];
  children: TraceTreeNode[] = [];
  parentId?: string;
  key?: string | number; // could be a field name or array index
  kind?: Types.TraceNodeKind;
  type?: string; // could be a field name or array index
  parent?: TraceTreeNode;
  startTime!: number; // TODO: this may not be safe, it is possibly undefined
  endTime!: number; // TODO: this may not be safe, it is possibly undefined
  isFetch?: boolean;
  isDeferredFetch?: boolean;
  isConnector?: boolean;
  subgraphName?: string;
  subgraphStartTime?: number;
  subgraphEndTime?: number;
  cacheMaxAgeMs?: number;
  showClockSkewWarning?: boolean;
  childCount?: number;
  childrenAreTruncated?: boolean;
  childrenAreFiltered?: boolean;
  isIncomplete?: boolean;
  errors: (
    | Types.TraceNodeErrorFragment
    | (Omit<ProtobufTrace.IError, 'location'> & {
        locations: ProtobufTrace.ILocation[];
      })
  )[] = [];
  path: TraceTreeNode[] = [];
  inherited: TraceTreeInheritedProperties = Object.create(null);

  constructor(node: Types.TraceNodeFragment);
  constructor(
    node: ProtobufTrace.INode,
    traceStartMS: number | undefined,
    parent: TraceTreeNode | undefined,
  );
  constructor(
    node: Types.TraceNodeFragment | ProtobufTrace.INode,
    traceStartMS?: number,
    parent?: TraceTreeNode,
  ) {
    if (typeof node.type === 'string') this.type = node.type;

    // Types.TraceNodeFragment type node
    if ('__typename' in node) {
      // we accept numbers (i.e. 0) as keys, so we need to check the type here
      if (typeof node.key === 'number' || typeof node.key === 'string') {
        this.key = node.key;
      }
      this.attributes = node.attributes.filter(
        ({ key }: { key: string }) => !key.startsWith('apollo_private'),
      );
      this.kind = node.kind;
      this.id = node.id;
      this.childCount = node.childCount;
      this.childrenAreTruncated = node.childrenAreTruncated;
      this.startTime = format.dateToReal(node.startTime);
      this.endTime = format.dateToReal(node.endTime);

      // we accept numbers (i.e. 0) as keys, so we need to check the type here
      if (typeof node.cacheMaxAgeMs === 'number') {
        this.cacheMaxAgeMs = node.cacheMaxAgeMs;
      }

      // we accept empty strings as IDs, so we need to check the type here
      if (typeof node.parentId === 'string') this.parentId = node.parentId;

      this.isFetch = node.isFetch;
      this.isDeferredFetch = node.isDeferredFetch;
      this.isConnector =
        typeof node.key === 'string' && isConnectorSubgraphViaName(node.key);
      this.subgraphName = node.subgraphName ?? undefined;
      if (node.subgraphStartTime && node.subgraphEndTime) {
        this.subgraphStartTime = format.dateToReal(node.subgraphStartTime);
        this.subgraphEndTime = format.dateToReal(node.subgraphEndTime);

        // Note that checkAndFixClockSkew is calledafter the entire tree has
        // been created and parent refs are valid. This will adjust the start
        // and end times in cases where the subgraph reported times are
        // different to the gateway reported times
      }

      if (node.errors) this.errors = node.errors;
    }
    // ProtobufTrace.INode type node
    else {
      if (node.responseName) {
        this.key = node.responseName;
      } else if (typeof node.index === 'number') {
        this.key = node.index;
      }
      this.id = `${parent?.id}-${this.key}`;

      if (typeof node.startTime === 'number') {
        this.startTime = format.dateToReal(
          node.startTime / 1000000 + (traceStartMS ?? 0),
        );
      }
      if (typeof node.endTime === 'number') {
        this.endTime = format.dateToReal(
          node.endTime / 1000000 + (traceStartMS ?? 0),
        );
      }

      // we accept numbers (i.e. 0) as keys, so we need to check the type here
      if (typeof node.cachePolicy?.maxAgeNs === 'number') {
        this.cacheMaxAgeMs = node.cachePolicy.maxAgeNs;
      }

      if (parent) {
        this.addParent(parent);
      }

      if (node.error) {
        this.errors = node.error?.map((error) => ({
          ...error,
          locations: error.location ?? [],
        }));
      }
    }
  }

  get name() {
    // account for arrays which need artificial names for nodes
    // we can assign these artificial names once we've linked parents
    if (typeof this.key === 'number' && this.parent) {
      return `${this.parent.key}.${this.key}`;
    }

    return this.key;
  }

  // Follows from the algorithm used in TraceNode.tsx.  That code
  // also depends on the "open" state of the node, which we don't have here,
  // but it sure would be nice to keep all the start / end time logic in one
  // place.  TODO(otel-traces): refactor to share the start / end time logic.
  get displayDuration() {
    return (
      this.fetchNodeDuration ??
      Math.max(this.nodeDuration, this.childSpansDuration)
    );
  }

  // The actual span / node duration.
  get nodeDuration() {
    return this.endTime - this.startTime;
  }

  // For fetch nodes, we trust the "subgraph" timings to give us the duration.
  // This hides the actual duration of the fetch node, but visibility here
  // will be improved with "OTel" traces.
  get fetchNodeDuration() {
    if (this.isFetch && !!this.subgraphStartTime && !!this.subgraphEndTime) {
      return this.subgraphEndTime - this.subgraphStartTime;
    }
    return null;
  }

  // For FTV1 / Apollo trace resolver timings, the duration of parent nodes
  // may not include child nodes, so we have to calculate this based on children.
  // This would be better done on the backend but handling it here for now.
  // TODO(otel-traces): refactor to share the start / end time logic.
  get childSpansDuration() {
    const descendants = this.getDescendants().filter((n) => n.nodeDuration > 0);
    return (
      Math.max(...descendants.map((n) => n.endTime)) -
      Math.min(...descendants.map((n) => n.startTime))
    );
  }

  get isRootNode() {
    return !this.parent && !this.parentId;
  }

  setPath(path: TraceTreeNode[]) {
    this.path = path;
  }

  addParent(node: TraceTreeNode) {
    this.parent = node;
    this.parentId = node.id;
    this.inherited = {
      isDeferred:
        this.inherited.isDeferred ||
        node.inherited.isDeferred ||
        node.isDeferredFetch,
    };
  }

  addChild(node: TraceTreeNode) {
    this.children.push(node);
    // eslint-disable-next-line no-param-reassign
    node.inherited = {
      isDeferred: this.inherited.isDeferred || this.isDeferredFetch,
    };
  }

  adoptChildren(node: TraceTreeNode) {
    node.children.forEach((child) => {
      child.addParent(this);
    });
    this.children = [
      ...this.children.filter((child) => child.id !== node.id),
      ...node.children,
    ];
    this.childrenAreFiltered = true;
  }

  /**
   * This is to fix the case where a subgraph's clock is different to the
   * gateway clock. For example, if the gateway says that it sent a request at
   * 12:00:00 and recieved a response at 12:00:01, but the subgraph says that it
   * started processing at 13:00:00.1 and finished at 13:00:00.9, we can assume
   * that the subgraph's clock is 1 hour ahead of the gateway. This function is
   * used to adjust the subgraph node times by the detected clock difference.
   */
  fixOffsetSubgraphClock(delta: number, minTime: number, maxTime: number) {
    this.startTime = _.clamp(this.startTime + delta, minTime, maxTime);
    this.endTime = _.clamp(this.endTime + delta, minTime, maxTime);
  }

  /**
   * Returns all descendent nodes, optionally including itself
   */
  getDescendants(excludeThis?: boolean): TraceTreeNode[] {
    const descendants = [...this.children.flatMap((n) => n.getDescendants())];
    if (!excludeThis) {
      descendants.unshift(this);
    }
    return descendants;
  }

  checkAndFixClockSkew(): void {
    if (this.isFetch && !!this.subgraphStartTime && !!this.subgraphEndTime) {
      if (
        this.subgraphStartTime < this.startTime ||
        this.subgraphEndTime > this.endTime
      ) {
        // Only show a warning if the times are significantly different (more
        // than 100ms), but adjust the times even if they are a little bit off
        // so the subgraph time bars fit inside the gateway time bars.
        const maxClockDiff = Math.max(
          Math.abs(this.startTime - this.subgraphStartTime),
          Math.abs(this.subgraphEndTime - this.endTime),
        );
        if (maxClockDiff > 100) {
          this.showClockSkewWarning = true;
        }

        // calculate the time taken in network requests (time taken as recorded
        // by the gateway minus time taken as recorded by the subgraph)
        const gatewayTime = this.endTime - this.startTime;
        const subgraphTime = this.subgraphEndTime - this.subgraphStartTime;
        const lagTime = Math.max(0, gatewayTime - subgraphTime);

        // Adjust times by the difference between gateway and subgraph start
        // time, plus half the lag time (since we don't know on which side the
        // lag occurred). Make sure we cap time at gateway end time in case of
        // super funky clocks.
        const startTimeDelta = this.startTime - this.subgraphStartTime;
        const adjustDelta = startTimeDelta + lagTime / 2;

        this.subgraphStartTime = Math.max(
          this.subgraphStartTime + adjustDelta,
          this.startTime,
        );
        this.subgraphEndTime = Math.min(
          this.subgraphEndTime + adjustDelta,
          this.endTime,
        );

        this.getDescendants(true).forEach((node) => {
          node.fixOffsetSubgraphClock(
            adjustDelta,
            this.startTime,
            this.endTime,
          );
        });
      }
    }
  }
}

/**
 * A litte class to build a Trace Tree out of the flat list of descendants we get
 * from the API.
 *
 * We want to avoid having to ask for a circular query with trace descendants from the backend,
 * so we recursively build this Tree on the frontend instead!
 */
export class TraceTree {
  root: TraceTreeNode;
  requestStart!: number; // TODO: this may not be safe, it is possibly undefined
  requestEnd!: number; // TODO: this may not be safe, it is possibly undefined
  criticalPath: TraceTreeNode[] = [];
  hasCachePolicy = false;
  hasRouterInternals = false;
  erroredNodes: TraceTreeNode[] = [];
  agentVersion: string = '';
  nodeCount = 1; // when we get here, we at least have the root node

  /** Memoize our node list so we can easily look up specific Nodes */
  private _nodeMap: { [id: string]: TraceTreeNode } = Object.create(null);
  /** Memoize all paths so we can easily look up the path for a node */
  private _paths: { [id: string]: TraceTreeNode[] } = Object.create(null);

  constructor(
    trace:
      | Types.TraceFragment
      | (ProtobufTrace & { root: NonNullable<ProtobufTrace['root']> }),
    filter?: ((node: TraceTreeNode) => boolean) | undefined,
  ) {
    if ('__typename' in trace) {
      /** Make a list of all the new Node class instances (our helper class) */
      const rootNode = new TraceTreeNode({
        ...trace.root,
        key: trace.operationName,
      });
      if (typeof rootNode.cacheMaxAgeMs === 'number') {
        this.hasCachePolicy = true;
      }
      this._nodeMap[rootNode.id] = rootNode;
      this.root = rootNode;

      if (trace.isIncomplete) {
        this.root.isIncomplete = true;
      }

      trace.root.descendants.forEach((n) => {
        if (typeof n.cacheMaxAgeMs === 'number') this.hasCachePolicy = true;
        if (n.kind === Types.TraceNodeKind.ROUTER_INTERNAL)
          this.hasRouterInternals = true;
        const node = new TraceTreeNode(n);
        this._nodeMap[n.id] = node;
        this.nodeCount++;
      });

      this.requestStart = format.dateToReal(trace.startTime);
      this.requestEnd = format.dateToReal(trace.endTime);
      this.agentVersion = trace.agentVersion || '';

      /** Read over each Node and link it to its parent and children Nodes */
      Object.values(this._nodeMap).forEach((node) => {
        const parent =
          node.parentId !== undefined && this._nodeMap[node.parentId];
        if (parent) {
          node.addParent(parent);
          parent.addChild(node);
        } else if (node.id !== trace.root.id) {
          // don't link the root to itself...
          this.root.addChild(node);
        }

        if (node.errors.length > 0) this.erroredNodes.push(node);
      });

      // check all immediate children for clock skew and adjust times if needed
      this.root.children.forEach((node) => node.checkAndFixClockSkew());
    } else {
      let traceStartms: number | undefined;
      if (trace.startTime?.seconds) {
        traceStartms =
          trace.startTime.seconds * 1000 +
          (trace.startTime.nanos ?? 0) / 1000000;
        this.requestStart = format.dateToReal(traceStartms);
      }
      if (trace.endTime?.seconds) {
        this.requestEnd = format.dateToReal(
          // TODO: remove this disabling rule for eslint v8 migration
          // eslint-disable-next-line no-unsafe-optional-chaining
          trace.endTime?.seconds * 1000 + (trace.endTime.nanos ?? 0) / 1000000,
        );
      }

      const walk = (
        protobufNode: ProtobufTrace.INode,
        parent: TraceTreeNode | undefined,
      ) => {
        const node = new TraceTreeNode(protobufNode, traceStartms, parent);

        if (typeof node.cacheMaxAgeMs === 'number') {
          this.hasCachePolicy = true;
        }
        if (node.errors.length > 0) this.erroredNodes.push(node);
        this._nodeMap[node.id] = node;
        this.nodeCount++;
        protobufNode.child?.forEach((child) =>
          node.addChild(walk(child, node)),
        );
        return node;
      };

      this.root = walk(trace.root, undefined);
    }

    if (filter) {
      /** Filter nodes and re-link tree */
      Object.values(this._nodeMap).forEach((node) => {
        if (!filter(node)) {
          const parent =
            node.parentId !== undefined && this._nodeMap[node.parentId];
          if (parent) {
            // We need to delete this node from the tree.
            // Move children to the parent.
            parent.adoptChildren(node);
          }
        }
      });
    }

    /**
     * BFS to memoize the paths of each Node in the tree.
     */
    const queue = [this.root];
    while (queue.length > 0) {
      const node = queue.shift();
      if (!node) break; // make TS happy, we as humans know this won't happen.

      const pathTo = node.parent ? this._paths[node.parent.id] : [];
      this._paths[node.id] = [...(pathTo ?? []), node];
      node.setPath([...(pathTo ?? []), node]);

      node.children.forEach((n) => {
        queue.push(n); // this is a tree, so we can do this happily
      });
    }

    const criticalPathNode = findCriticalPath(this.root);
    const criticalPath = this._paths[criticalPathNode.id];
    if (criticalPath === undefined) {
      throw new Error(`Expected a path for node ${criticalPathNode.id}`);
    }
    this.criticalPath = criticalPath;
  }

  /**
   * As of now, there should always be a root node + 1 or more descendants
   * in the case of a non-errored / non-cached trace.
   */
  get isIncomplete() {
    return (
      this.nodeCount <= 1 &&
      this.erroredNodes.length === 0 &&
      !this.hasCachePolicy
    );
  }

  flatten(filter: ((node: TraceTreeNode) => boolean) | null = null) {
    return flatten(filter, this.root);
  }
}

/**
 * DFS to find the "critical path" node of the tree.
 */
function findCriticalPath(node: TraceTreeNode): TraceTreeNode {
  if (node.children.length > 0) {
    // Keep track of the child with highest duration in the critical path
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const maxChild = _.maxBy(node.children, (n) => n.displayDuration)!;
    return findCriticalPath(maxChild);
  }
  return node;
}

/**
 * Reduce the tree to an array of nodes.
 * @param filter If set, only include nodes passing the filter function.
 * @param node The root node of the tree.
 * @param acc Destination array used for accumulating nodes.
 */
export function flatten(
  filter: ((node: TraceTreeNode) => boolean) | null = null,
  node: TraceTreeNode,
): TraceTreeNode[] {
  return [
    ...(!filter || filter(node) ? [node] : []),
    ...node.children.flatMap((child) => flatten(filter, child)),
  ];
}
